<template>
  <footer class="mt-auto border-t border-jade-800 bg-jade-950 text-jade-100">
    <div class="mx-auto max-w-7xl px-6 py-12 lg:py-14">
      <div class="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
        <div class="lg:col-span-1">
          <router-link :to="homePath" class="inline-block">
            <img src="/img/logos/logo-white.svg" class="h-10 w-44" alt="True Market Insiders">
          </router-link>
          <p class="mt-4 max-w-xs text-base leading-relaxed text-jade-200/90">
            Sector research, education, and tools from Chris Rowe and the True Market Insiders team.
          </p>
          <div class="mt-6 flex gap-3">
            <a
              v-for="item in social"
              :key="item.key"
              :href="item.path"
              class="flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-lg text-white transition hover:bg-jade-600"
              target="_blank"
              rel="noopener noreferrer"
            >
              <span class="sr-only">{{ labelService.label( item.key ) }}</span>
              <i :class="`pi ${ item.icon }`"></i>
            </a>
          </div>
        </div>

        <div>
          <h2 class="text-xl sm:text-2xl font-bold uppercase tracking-wider text-jade-300">Explore</h2>
          <ul class="mt-4 space-y-3 text-lg">
            <li><router-link :to="articleSearchPath" class="text-jade-100 hover:text-white no-underline">Articles</router-link></li>
            <li><router-link :to="sectorEdgePath" class="text-jade-100 hover:text-white no-underline">Sector Edge</router-link></li>
            <li><router-link :to="contactUsPath" class="text-jade-100 hover:text-white no-underline">Contact Us</router-link></li>
            <li><router-link :to="signInPath" class="font-semibold text-white hover:text-jade-200 no-underline">Member Log In</router-link></li>
          </ul>
        </div>

        <div class="lg:col-span-2">
          <h2 class="text-xl sm:text-2xl font-bold uppercase tracking-wider text-jade-300">Contact</h2>
          <div class="mt-4 grid gap-4 sm:grid-cols-2 text-lg">
            <div>
              <p class="font-semibold text-white">Phone</p>
              <p class="mt-2 text-jade-200">
                <a href="tel:+18558220269" class="text-inherit hover:text-white no-underline">(855) 822-0269</a>
                <span class="block text-base text-jade-300">U.S. &amp; Canada</span>
              </p>
              <p class="mt-3 text-jade-200">
                <a href="tel:+15612061207" class="text-inherit hover:text-white no-underline">+1 (561) 206-1207</a>
                <span class="block text-base text-jade-300">International</span>
              </p>
            </div>
            <div>
              <p class="font-semibold text-white">Email</p>
              <p class="mt-2">
                <a href="mailto:support@truemarketinsiders.com" class="text-jade-100 hover:text-white no-underline break-all">
                  support@truemarketinsiders.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="border-t border-white/10 bg-black/40">
      <div class="mx-auto flex max-w-7xl flex-col items-center gap-4 px-6 py-6 text-center sm:flex-row sm:justify-between sm:text-left">
        <!-- UX roadmap #41: below sm each legal link is a 40px row target (it was its 20px line); the padding takes over
             from the row gap there. -->
        <nav class="flex flex-wrap justify-center gap-x-6 gap-y-2 max-sm:gap-y-0" aria-label="Legal">
          <router-link
            v-for="item in legal"
            :key="item.key"
            :to="item.path"
            class="text-sm text-jade-300 hover:text-white no-underline max-sm:py-2.5"
          >
            {{ labelService.label( item.key ) }}
          </router-link>
        </nav>
        <p class="text-sm text-jade-400 mb-0" data-testid="footer-version">
          v{{ currentVersion }} © True Market Insiders 2016-{{ new Date().getFullYear() }}
        </p>
      </div>
    </div>
  </footer>
</template>

<script setup lang="ts">
import type { LabelService } from "@/composables/label-service";
import { labelServiceSymbol } from "@/plugins/LabelServicePlugin";
import routeManager from "@/router/route-manager";
import { useVersionInfo } from "@/services/version-service";
import { computed, inject } from "vue";

const labelService = inject< LabelService >( labelServiceSymbol ) as LabelService;

const { currentVersion } = useVersionInfo();
const legal = computed( () => routeManager.legalNavigation() );
const social = computed( () => routeManager.socialNavigation() );

const homePath = routeManager.homePath();
const articleSearchPath = routeManager.articleSearchPath();
const sectorEdgePath = routeManager.sectorEdgePath();
const contactUsPath = routeManager.contactUsPath();
const signInPath = routeManager.signInPath();
</script>
